<script setup lang="ts">
  /**
   * PDFViewer Component with Batch Loading and Proper Zoom Calculation
   * A reusable component for displaying PDF documents in a modal dialog with optimized memory usage.
   *
   * Features:
   * - Modal PDF viewer with backdrop click to close
   * - Batch loading of pages to prevent memory issues
   * - Virtual scrolling for large documents
   * - Page navigation with input field and arrow buttons
   * - Page navigation with scroll position tracking
   * - Keyboard navigation (arrow keys for pages, Escape to close)
   * - Text highlighting
   * - Per-page rotation (90° increments)
   * - Loading state with progress
   * - Responsive design
   * - Fit page enabled by default for optimal viewing
   * - Automatic scanned PDF detection with fit page
   * - Accurate zoom level calculation and display
   * - Proper zoom transitions between fit page and manual zoom
   *
   * @example
   * ```vue
   *   <PDFViewer
   *     v-model:isOpen="isOpen"
   *     v-model:url="pdfUrl"
   *     :filename="filename"
   *     :highlight-text="'text to highlight'"
   *     :initial-page="1"
   *     :batch-size="5"
   *     @loaded="(pdfInstance) => console.log('PDF loaded', pdfInstance)"
   *     @page-change="(page) => console.log('Page changed', page)"
   *   />
   * ```
   *
   * @typedef {Object} Props
   * @property {string} url - URL of the PDF file to display
   * @property {string} filename - Name of the file to display in the header
   * @property {boolean} isOpen - Controls visibility of the modal
   * @property {number} [initialPage=1] - Initial page number to display
   * @property {string} [highlightText] - Text to highlight in the PDF
   * @property {number} [scale=1.1] - Scale factor for the PDF display
   * @property {boolean} [fitToWidth=true] - Whether to fit the PDF to the width of the container
   * @property {Object} [document] - Document metadata for scanned PDF detection
   * @property {number} [document.filesize] - File size in bytes (required for detection)
   * @property {number} [document.page_count] - Number of pages in the document (optional, improves detection accuracy)
   * @property {boolean} [annotation=false] - Whether to enable annotations
   * @property {string} [formId] - ID of the form to load annotations for
   * @property {boolean} [hideNavigation=false] - Whether to hide the navigation controls
   * @property {boolean} [disableEditPage=false] - Whether to disable the edit page button on hover
   * @property {string} [isFilledBy] - Whether to show the fields filled by lawyer or client, leave empty to show all
   *
   * @typedef {Object} Emits
   * @property {(event: 'update:isOpen', value: boolean) => void} update:isOpen - Emitted when modal visibility changes
   * @property {(event: 'update:url', value: string) => void} update:url - Emitted when URL should be updated
   * @property {(event: 'loaded', pdfInstance: any) => void} loaded - Emitted when PDF is loaded
   * @property {(event: 'pageChange', page: number) => void} pageChange - Emitted when page changes
   * @property {(event: 'highlight', event: any) => void} highlight - Emitted on text highlight
   */

  import PDFNavigation from '@/components/pdf/genericPdfNav.vue'
  import Tooltip from '@/components/ui/tooltip/Tooltip.vue'
  import TooltipContent from '@/components/ui/tooltip/TooltipContent.vue'
  import TooltipProvider from '@/components/ui/tooltip/TooltipProvider.vue'
  import TooltipTrigger from '@/components/ui/tooltip/TooltipTrigger.vue'
  import { usePdfEditor, type CroppedPageData } from '@/composables/usePdfEditor'
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import { downloadDocument } from '@/lib/utils'
  import { useNewPdfStore } from '@/store/newPdfStore'
  import { useSignatures } from '@/store/signatureStore'
  import { DocumentsService } from '@/types/generated'
  import { VuePDF, type AnnotationEventPayload } from '@tato30/vue-pdf'
  import { usePDFWithCmaps as usePDF } from '@/composables/usePDFWithCmaps'
  import {
    Download,
    FilePen,
    FileStack,
    Maximize,
    MoreHorizontal,
    Undo2,
    X,
    ZoomIn,
    ZoomOut
  } from 'lucide-vue-next'
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import ImageEditorModal from './image-editor/ImageEditorModal.vue'
  import FormFieldOverlayItem from '@/components/forms/FormFieldOverlayItem.vue'
  import SignModalV3 from '@/components/pdf/SignModalV3.vue'

  interface PageRegion {
    id?: string
    top_left: { x: number; y: number }
    top_right: { x: number; y: number }
    bottom_left: { x: number; y: number }
    bottom_right: { x: number; y: number }
  }

  interface Props {
    url: string
    filename: string
    isOpen: boolean
    initialPage?: number
    highlightText?: string
    scale?: number
    fitPage?: boolean
    batchSize?: number
    preloadBuffer?: number
    document?: {
      filesize?: number
      page_count?: number
      uuid?: string
      is_pdf_modified?: boolean
      current_pdf_url?: string
      original_pdf_url?: string
    }
    annotation?: boolean
    formId?: string
    hideNavigation?: boolean
    hideTopbar?: boolean
    disableEditPage?: boolean
    shouldPersist?: boolean
    formFields?: any[]
    showFormFields?: boolean
    pdfTemplate?: any
    isFilledBy?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    batchSize: 5,
    preloadBuffer: 2,
    fitPage: true,
    hideTopbar: false,
    disableEditPage: false,
    shouldPersist: false,
    showFormFields: false,
    isFilledBy: ''
  })

  const emit = defineEmits<{
    (e: 'update:isOpen', value: boolean): void
    (e: 'update:url', value: string): void
    (e: 'loaded', pdfInstance: any): void
    (e: 'pageChange', page: number): void
    (e: 'highlight', event: any): void
    (e: 'document-updated', document: any): void
  }>()

  const { t } = useTranslation({
    en: {
      thisPage: 'This page',
      allPages: 'All pages',
      rotateLeft: 'Rotate left',
      rotateRight: 'Rotate right',
      highContrast: 'High contrast',
      loadingDocument: 'Loading document...',
      percentLoaded: '% loaded',
      region: 'Region',
      clickToLoad: 'Click to load',
      editTitle: 'Edit title',
      close: 'Close',
      moreOptions: 'More options',
      applyingChanges: 'Applying changes...',
      processingPdf: 'Processing and uploading modified PDF',
      revertingChanges: 'Reverting changes...',
      loadingOriginalPdf: 'Loading original PDF',
      organizePages: 'Organize pages',
      editPage: 'Edit page'
    },
    de: {
      thisPage: 'Diese Seite',
      allPages: 'Alle Seiten',
      rotateLeft: 'Links drehen',
      rotateRight: 'Rechts drehen',
      highContrast: 'Hoher Kontrast',
      loadingDocument: 'Dokument wird geladen...',
      percentLoaded: '% geladen',
      region: 'Bereich',
      clickToLoad: 'Zum Laden klicken',
      editTitle: 'Titel bearbeiten',
      close: 'Schließen',
      moreOptions: 'Weitere Optionen',
      applyingChanges: 'Änderungen werden angewendet...',
      processingPdf: 'PDF wird verarbeitet und hochgeladen',
      revertingChanges: 'Änderungen werden rückgängig gemacht...',
      loadingOriginalPdf: 'Original-PDF wird geladen',
      organizePages: 'Seiten organisieren',
      editPage: 'Seite bearbeiten'
    }
  })

  const router = useRouter()
  const { trackEvent } = useMetrics()
  const currentPage = ref(props.initialPage || 1)
  const isLoading = ref(true)
  const loadingProgress = ref(0)
  const internalUrl = ref(props.url)
  const currentScale = ref(props.scale ?? 1.0)
  const fitPage = ref(props.fitPage ?? true)

  // PDF version management
  const originalPdfUrl = ref(props.url)
  const modifiedPdfUrl = ref('')
  const isModified = ref(false)

  // Flag to prevent cascading watcher triggers during document updates
  const isUpdatingFromBackend = ref(false)

  // Computed property to check if document has any modifications (local or backend)
  const hasAnyModifications = computed(() => {
    // Check if there are local modifications (frontend-only)
    if (isModified.value) return true

    // Check if document has backend modifications
    if (props.document?.is_pdf_modified) return true

    return false
  })

  // Computed property to check if document has backend modifications only
  const hasBackendModifications = computed(() => {
    return props.document?.is_pdf_modified && !isModified.value
  })
  const containerRef = ref<HTMLElement | null>(null)
  const scrollContainerRef = ref<HTMLElement | null>(null)
  const pageRefs = ref<Map<number, HTMLElement>>(new Map())
  const isScrolling = ref(false)
  const isActivelyScrolling = ref(false)
  const scrollStopTimeout = ref<NodeJS.Timeout | null>(null)
  const loadedPages = ref<Set<number>>(new Set())
  const pdfKey = ref(0)
  const hasInitialScrolled = ref(false)
  const pageRotations = ref<Map<number, number>>(new Map())

  // Track virtual page rotations (separate from original page rotations)
  const virtualPageRotations = ref<Map<string, number>>(new Map())

  // Track the actual rendered scale for each page (from viewport)
  const pageScales = ref<Map<number, number>>(new Map())
  const containerWidth = ref(0)
  const containerHeight = ref(0)
  const calculatedFitScale = ref<number>(1.0)

  // Page detection hysteresis to prevent jumping between pages
  const lastStablePage = ref(1)
  const pageChangeCooldown = ref(0)

  // Scale calculation debouncing
  const scaleCalculationTimeout = ref<NodeJS.Timeout | null>(null)

  // Batch loading state
  const visiblePages = ref<Set<number>>(new Set())
  const loadingQueue = ref<number[]>([])
  const isLoadingBatch = ref(false)
  const totalPages = ref(0)
  const pageHeights = ref<Map<number, string>>(new Map())
  const loadingTimeout = ref<NodeJS.Timeout | null>(null)
  const showEditMenu = ref(false)
  const editMode = ref<'this-page' | 'all-pages'>('this-page')
  const highContrastPages = ref<Map<number, boolean>>(new Map())
  const showActionsMenu = ref(false)
  const showResetConfirmModal = ref(false)
  const isRevertingChanges = ref(false)

  // Virtual page mapping
  const virtualPages = ref<virtual_page[]>([])
  const currentVirtualPage = ref(1)
  const totalVirtualPages = computed(() => virtualPages.value.length)

  // Image Editor State
  const showImageEditor = ref(false)
  const capturedPageImage = ref('')
  const isCapturingPage = ref(false)
  const isApplyingChanges = ref(false)

  // Temporary stubs for removed edge adjustment functionality
  const customRegions = ref(new Map())
  const isEdgeAdjustMode = ref(false)
  const isDrawing = ref(false)
  const currentDrawRect = ref(null)
  const regionOverlay = ref(null)
  const hasUnsavedChanges = ref(false)
  const showApplyChangesDialog = ref(false)
  const originalRegionsState = ref(new Map())

  interface virtual_page {
    id: string
    originalPage: number
    region: PageRegion
    virtualIndex: number
  }

  // Computed property to get the correct PDF URL (prefer local modifications over backend)
  const currentPdfUrl = computed(() => {
    // If we have a local modified version (blob URL), always use that first
    if (isModified.value && modifiedPdfUrl.value) {
      return modifiedPdfUrl.value
    }

    // If document has backend modifications, use current_pdf_url
    if (props.document?.is_pdf_modified && props.document?.current_pdf_url) {
      return props.document.current_pdf_url
    }

    // Otherwise use the internal URL (which could be original or updated)
    return internalUrl.value
  })

  // Use current PDF URL for PDF loading with reactive key for forcing reloads
  const pdfSource = computed(() => {
    const url = currentPdfUrl.value
    if (!url) return ''

    let finalUrl = ''

    // For blob URLs, just return the URL directly - the pdfKey reactivity will be handled by URL changes
    if (url.startsWith('blob:')) {
      finalUrl = url
    } else {
      // For regular URLs, add query parameters for cache busting
      const separator = url.includes('?') ? '&' : '?'
      finalUrl = `${url}${separator}key=${pdfKey.value}`
    }

    return finalUrl
  })
  const { pdf, pages } = usePDF(pdfSource)

  // Calculate the effective zoom level for display
  const effectiveZoom = computed(() => {
    if (fitPage.value) {
      // Use the calculated fit scale
      return calculatedFitScale.value
    }
    return currentScale.value
  })

  // High contrast classes per page
  const getHighContrastClasses = (pageNumber: number) => {
    return highContrastPages.value.get(pageNumber) ? 'contrast-150 brightness-110 grayscale' : ''
  }

  // Display zoom percentage
  const displayZoom = computed(() => {
    return Math.round(effectiveZoom.value * 100)
  })

  // Check if PDF is ready for editing operations (edge adjust, page editing, etc.)
  const isPdfReady = computed(() => {
    const ready = !!(
      pdf.value &&
      totalPages.value > 0 &&
      currentPage.value >= 1 &&
      currentPage.value <= totalPages.value
    )

    return ready
  })

  // Detect if PDF is scanned (image-based) based on filesize per page
  const isScannedPDF = computed(() => {
    const doc = props.document
    if (!doc || !doc.filesize) return false

    if (doc.page_count && doc.page_count > 0) {
      const fileSizePerPage = doc.filesize / doc.page_count
      return fileSizePerPage > 1000000
    }

    return doc.filesize > 5000000
  })

  const highlightOptions = computed(() => ({
    ignoreCase: true,
    completeWords: true
  }))

  // Field values state for fillable mode
  const fieldValues = ref<Record<string, any>>({})

  // Signature modal state
  const showSignatureModal = ref(false)
  const currentSignatureField = ref<any>(null)

  // Handle field value updates
  const updateFieldValue = (fieldId: string | undefined, value: any) => {
    if (!fieldId) return
    fieldValues.value[fieldId] = value
  }

  // Handle signature field click
  const handleSignatureClick = (field: any) => {
    currentSignatureField.value = field
    showSignatureModal.value = true

    // Also set the signature store modal state
    signatureStore.showSignModal = true
  }

  // Reset modal state initially
  const resetModalState = () => {
    showSignatureModal.value = false
    currentSignatureField.value = null
    signatureStore.showSignModal = false
  }

  // Get field value
  const getFieldValue = (fieldId: string | undefined) => {
    if (!fieldId) return ''
    return fieldValues.value[fieldId] || ''
  }

  // Get signature data for a field
  const getSignatureData = (field: any) => {
    if (props.formId && field?.name) {
      const signatureKey = props.formId + field.name
      const signature = signatureStore.signatures[signatureKey]
      return signature?.data_uri || ''
    }

    return ''
  }

  // Calculate which pages should be visible based on current page and buffer
  const getVisiblePageRange = (centerPage: number) => {
    const start = Math.max(1, centerPage - props.preloadBuffer)
    const end = Math.min(totalPages.value, centerPage + props.preloadBuffer)
    return Array.from({ length: end - start + 1 }, (_, i) => start + i)
  }

  // Function to calculate container dimensions
  function updateContainerWidth() {
    if (containerRef.value && scrollContainerRef.value) {
      // Account for padding and margins
      const rect = containerRef.value.getBoundingClientRect()
      containerWidth.value = rect.width - 48 // Subtract padding (24px * 2)

      // Calculate available height: scroll container height minus padding
      const scrollRect = scrollContainerRef.value.getBoundingClientRect()
      containerHeight.value = scrollRect.height - 48 // Subtract padding (24px top + 24px bottom)
    }
  }

  function debouncedCalculateFitToPageScale() {
    if (scaleCalculationTimeout.value) {
      clearTimeout(scaleCalculationTimeout.value)
    }

    scaleCalculationTimeout.value = setTimeout(() => {
      calculateFitToPageScale()
    }, 100)
  }

  async function calculateFitToPageScale() {
    if (isApplyingChanges.value || isRevertingChanges.value || isUpdatingFromBackend.value) {
      return
    }

    if (!pdf.value || !fitPage.value) {
      return
    }

    if (isActivelyScrolling.value) {
      return
    }

    try {
      let pdfDocument: any = pdf.value
      if (
        pdf.value &&
        typeof (pdf.value as any).getPage !== 'function' &&
        (pdf.value as any).promise
      ) {
        pdfDocument = await (pdf.value as any).promise
      }

      if (!pdfDocument || typeof pdfDocument.getPage !== 'function') {
        return
      }

      if (isApplyingChanges.value || isRevertingChanges.value || isUpdatingFromBackend.value) {
        return
      }

      const page = await pdfDocument.getPage(currentPage.value)
      const viewport = page.getViewport({ scale: 1.0 })

      const availableWidth = containerWidth.value
      const availableHeight = containerHeight.value

      if (availableWidth <= 0 || availableHeight <= 0) {
        return
      }

      const scaleX = availableWidth / viewport.width
      const scaleY = availableHeight / viewport.height
      const newScale = Math.min(scaleX, scaleY)

      const scaleDifference = Math.abs(newScale - calculatedFitScale.value)
      const scaleThreshold = 0.01

      if (scaleDifference > scaleThreshold) {
        calculatedFitScale.value = newScale
      }
    } catch (error) {
      if (!isApplyingChanges.value && !isRevertingChanges.value && !isUpdatingFromBackend.value) {
        console.error('Error calculating fit scale:', error)
      }
    }
  }

  // Watch for URL changes from parent
  watch(
    () => props.url,
    (newUrl, oldUrl) => {
      if (newUrl !== oldUrl && !isUpdatingFromBackend.value) {
        // Update original PDF URL and reset modification state
        originalPdfUrl.value = props.document?.original_pdf_url || newUrl

        // Use current_pdf_url if document has backend modifications, otherwise use newUrl
        const urlToUse = props.document?.current_pdf_url || newUrl
        internalUrl.value = urlToUse

        // Clean up any modified PDF
        if (modifiedPdfUrl.value) {
          URL.revokeObjectURL(modifiedPdfUrl.value)
          modifiedPdfUrl.value = ''
        }
        isModified.value = false

        if (newUrl) {
          fitPage.value = props.fitPage ?? true
          currentScale.value = props.scale ?? 1.0
          pageRotations.value.clear()
          virtualPageRotations.value.clear()
          pageScales.value.clear()
          highContrastPages.value.clear()
          editMode.value = 'this-page'
          hasInitialScrolled.value = false
          resetBatchLoading()
          // Force re-render when URL changes
          pdfKey.value++
        }
      }
    },
    { immediate: true }
  )

  // Watch for specific document properties that matter for PDF loading
  // Use separate watchers to avoid unnecessary deep watching
  watch(
    () => props.document?.current_pdf_url,
    (newUrl, oldUrl) => {
      if (newUrl !== oldUrl && !isUpdatingFromBackend.value) {
        // Only update and reload if the URL is actually different from our internal URL
        if (newUrl && newUrl !== internalUrl.value) {
          internalUrl.value = newUrl
          pdfKey.value++
        }
      }
    }
  )

  watch(
    () => props.document?.is_pdf_modified,
    (newModified, oldModified) => {
      if (newModified !== oldModified && props.document && !isUpdatingFromBackend.value) {
        // Update internalUrl to the correct URL based on modification status
        if (newModified && props.document.current_pdf_url) {
          internalUrl.value = props.document.current_pdf_url
        } else if (!newModified) {
          // Prefer current_pdf_url if available (e.g., preview blob URL),
          // otherwise fall back to original_pdf_url
          if (props.document.current_pdf_url) {
            internalUrl.value = props.document.current_pdf_url
          } else if (props.document.original_pdf_url) {
            internalUrl.value = props.document.original_pdf_url
          }
        }

        // Force a PDF reload to pick up the new URL
        pdfKey.value++
      }
    }
  )

  // Watch for modal open/close state
  watch(
    () => props.isOpen,
    (isOpen, wasOpen) => {
      if (isOpen && !wasOpen && props.url) {
        // Modal is opening - force complete reset

        isLoading.value = true
        totalPages.value = 0
        resetBatchLoading()

        // Prevent background scrolling
        document.body.style.overflow = 'hidden'

        // Reset zoom calculation values
        pageScales.value.clear()

        // Reset page detection hysteresis
        lastStablePage.value = props.initialPage || 1
        pageChangeCooldown.value = 0
        // Reset modal state
        resetModalState()

        signatureStore.setPdfStore('new')

        // Force PDF reload by changing the key
        pdfKey.value++

        // Set URL after key change to trigger usePDF reload
        nextTick(() => {
          // Use current_pdf_url if document has backend modifications, otherwise use props.url
          const urlToUse = props.document?.current_pdf_url || props.url
          internalUrl.value = urlToUse

          // Update original PDF URL reference
          originalPdfUrl.value = props.document?.original_pdf_url || props.url

          updateContainerWidth()
        })
      } else if (!isOpen && wasOpen) {
        // Modal is closing - restore background scrolling
        document.body.style.overflow = ''

        signatureStore.cleanup()
      }
    }
  )

  watch(
    () => props.fitPage,
    (newValue, oldValue) => {
      if (newValue !== oldValue) {
        fitPage.value = newValue
        // Clear stored page heights when fit page changes to prevent spacing issues
        pageHeights.value.clear()
      }
    }
  )

  // Watch for scale prop changes from parent
  watch(
    () => props.scale,
    (newScale, oldScale) => {
      if (newScale !== oldScale && newScale !== undefined) {
        currentScale.value = newScale
        // Clear stored page heights when scale changes to prevent spacing issues
        pageHeights.value.clear()
      }
    }
  )

  // Watch for initialPage changes from parent
  watch(
    () => props.initialPage,
    (newPage) => {
      if (newPage && newPage !== currentPage.value) {
        currentPage.value = newPage
        hasInitialScrolled.value = false
        updateVisiblePages(newPage)
        if (!isLoading.value && pageRefs.value.size > 0) {
          nextTick(() => {
            setTimeout(() => {
              scrollToPage(newPage)
            }, 100)
          })
        }
      }
    }
  )

  // Auto-enable fit page for scanned PDFs (now enabled by default)
  watch(
    [isScannedPDF, () => props.url],
    ([isScanned, url]) => {
      // Since fit page is now enabled by default, we only need to ensure it stays enabled for scanned PDFs
      if (url && isScanned && !fitPage.value) {
        fitPage.value = true
        // Don't increment pdfKey - just let the component re-render
      }
    },
    { immediate: true }
  )

  watch(currentPage, (newPage) => {
    updateVisiblePages(newPage)
    if (fitPage.value) {
      debouncedCalculateFitToPageScale()
    }
  })

  watch(fitPage, (newValue) => {
    if (newValue) {
      debouncedCalculateFitToPageScale()
    }
  })

  watch([containerWidth, containerHeight], () => {
    if (fitPage.value) {
      debouncedCalculateFitToPageScale()
    }
  })

  // Watch for page changes to update high contrast display
  watch(
    [currentPage, editMode, highContrastPages],
    () => {
      // The reactive getHighContrastClasses function will automatically
      // apply/remove high contrast as the user navigates between pages
    },
    { immediate: true }
  )

  // Watch for virtual pages changes to update navigation
  watch(
    virtualPages,
    () => {
      // Ensure current virtual page is valid
      if (virtualPages.value.length > 0 && currentVirtualPage.value > virtualPages.value.length) {
        currentVirtualPage.value = virtualPages.value.length
      }
    },
    { deep: true }
  )

  // Watch for PDF changes to reset loading state
  watch(pdf, (newPdf, oldPdf) => {
    if (newPdf && newPdf !== oldPdf) {
      isLoading.value = true
      totalPages.value = 0
      resetBatchLoading()

      // Check if pages are already available
      if (pages.value && pages.value > 0) {
        totalPages.value = pages.value
        nextTick(() => {
          initializeBatchLoading()
        })
      }
    } else if (!newPdf && oldPdf) {
      isLoading.value = true
      totalPages.value = 0
      resetBatchLoading()
    }
  })

  // Watch for pages count to initialize batch loading
  watch(
    pages,
    (newPages, oldPages) => {
      if (newPages && newPages > 0 && newPages !== oldPages) {
        totalPages.value = newPages
        nextTick(() => {
          initializeBatchLoading()
        })
      }
    },
    { immediate: true }
  )

  function resetBatchLoading() {
    loadedPages.value.clear()
    visiblePages.value.clear()
    loadingQueue.value = []
    isLoadingBatch.value = false
    loadingProgress.value = 0
    pageHeights.value.clear()

    // Clear any existing loading timeout
    if (loadingTimeout.value) {
      clearTimeout(loadingTimeout.value)
      loadingTimeout.value = null
    }
  }

  function initializeBatchLoading() {
    // Start with the initial page and its buffer
    const initialPage = props.initialPage || currentPage.value || 1

    // Ensure the current page is set to the initial page
    currentPage.value = initialPage

    updateVisiblePages(initialPage)

    // Set a fallback timeout to hide loading state if it gets stuck
    if (loadingTimeout.value) {
      clearTimeout(loadingTimeout.value)
    }
    loadingTimeout.value = setTimeout(() => {
      if (isLoading.value) {
        isLoading.value = false
      }
    }, 10000)
  }

  function updateVisiblePages(centerPage: number) {
    // If we have virtual pages, skip complex visibility logic (just render all)
    if (virtualPages.value.length > 0) {
      return
    }

    // Original page logic for non-virtual pages
    const newVisiblePages = new Set(getVisiblePageRange(centerPage))

    // Add pages that should be visible but aren't loaded yet to the queue
    const pagesToLoad = Array.from(newVisiblePages).filter((page) => !loadedPages.value.has(page))

    if (pagesToLoad.length > 0) {
      // Add to queue if not already there
      pagesToLoad.forEach((page) => {
        if (!loadingQueue.value.includes(page)) {
          loadingQueue.value.push(page)
        }
      })

      // Process the queue
      processLoadingQueue()
    }

    visiblePages.value = newVisiblePages
  }

  async function processLoadingQueue() {
    if (isLoadingBatch.value || loadingQueue.value.length === 0) {
      return
    }

    isLoadingBatch.value = true

    // Process pages in batches
    while (loadingQueue.value.length > 0) {
      const batch = loadingQueue.value.splice(0, props.batchSize)
      await loadPageBatch(batch)

      loadingProgress.value = Math.round((loadedPages.value.size / totalPages.value) * 100)
      await new Promise((resolve) => setTimeout(resolve, 50))
    }

    isLoadingBatch.value = false
  }

  async function loadPageBatch(pageNumbers: number[]) {
    // For this implementation, we're marking pages as "loaded" when they're in the visible set
    // The actual VuePDF component will handle the loading when it's rendered
    pageNumbers.forEach((page) => {
      if (page >= 1 && page <= totalPages.value) {
        loadedPages.value.add(page)
      }
    })

    // Small delay to allow DOM updates
    await new Promise((resolve) => setTimeout(resolve, 10))
  }
  const pdfStore = useNewPdfStore()
  const signatureStore = useSignatures()
  const pdfEditor = usePdfEditor()
  const withAnnotations = props.annotation
  const pdfStateAnnotationMap = computed(() => pdfStore.getAnnotationsMap(props.formId || ''))

  const onAnnotation = (payload: AnnotationEventPayload) => {
    if (!withAnnotations || !props.formId) return
    pdfStore.updateAnnotation(props.formId, payload)
    pdfStore.setManuallyEdited(props.formId, true)
  }

  function saveSignaturesToStore() {
    if (!props.formId) return

    const formSignatures = signatureStore.getFormSignatures(props.formId)

    if (Object.keys(formSignatures).length > 0) {
      pdfStore.updateSignatures(props.formId, formSignatures)
    }
  }

  function closeViewer() {
    if (props.formId && props.annotation) {
      saveSignaturesToStore()
    }

    // Restore background scrolling
    document.body.style.overflow = ''

    emit('update:isOpen', false)

    // Clear URL immediately to force usePDF to reset
    internalUrl.value = ''
    emit('update:url', '')

    // Reset all state
    currentPage.value = props.initialPage || 1
    currentVirtualPage.value = 1
    isLoading.value = true
    totalPages.value = 0
    resetBatchLoading()
    pageRotations.value.clear()
    virtualPageRotations.value.clear()
    pageScales.value.clear()
    highContrastPages.value.clear()
    virtualPages.value = []
    editMode.value = 'this-page'
    hasInitialScrolled.value = false
    pageRefs.value.clear()
    containerWidth.value = 0

    // Keep isLoadingState = true to prevent any saves during cleanup
    // It will be reset when the component is unmounted or when modal opens again
  }

  function onPdfLoaded(viewport: any, pageNumber: number) {
    // Store the actual scale from the viewport
    if (viewport && viewport.scale) {
      pageScales.value.set(pageNumber, viewport.scale)
    }

    // Store the actual height of loaded pages for better placeholders
    nextTick(() => {
      setTimeout(() => {
        const pageElement = pageRefs.value.get(pageNumber)
        if (pageElement) {
          const height = pageElement.offsetHeight
          if (height > 0) {
            pageHeights.value.set(pageNumber, `${height}px`)
          }
        }
      }, 50)
    })

    if (pageNumber === 1 && withAnnotations && props.formId) {
      try {
        if (pdf.value) {
          pdf.value.promise
            .then(async (pdfDoc: any) => {
              if (pdfDoc && typeof pdfDoc.getFieldObjects === 'function') {
                const fieldObjects = await pdfDoc.getFieldObjects()
                if (fieldObjects) {
                  await signatureStore.updateAll(fieldObjects as any)
                  await signatureStore.findAllSignatureFields(fieldObjects as any)
                }
              }
            })
            .catch((error: any) => {
              console.warn('Error handling signature fields:', error)
            })
        }
      } catch (error) {
        console.warn('Error handling signature fields:', error)
      }
    }

    // Mark this specific page as loaded
    const targetPage = props.initialPage || currentPage.value || 1

    // Hide loading state when the target page loads OR when the first page loads (whichever comes first)
    if (pageNumber === targetPage || loadedPages.value.size === 1) {
      isLoading.value = false

      // Clear the loading timeout since we successfully loaded
      if (loadingTimeout.value) {
        clearTimeout(loadingTimeout.value)
        loadingTimeout.value = null
      }

      emit('loaded', viewport)

      if (fitPage.value && !isApplyingChanges.value && !isRevertingChanges.value) {
        debouncedCalculateFitToPageScale()
      }
    }

    // Handle scrolling to initial page when it loads
    if (props.initialPage && pageNumber === props.initialPage && !hasInitialScrolled.value) {
      hasInitialScrolled.value = true
      nextTick(() => {
        setTimeout(() => {
          scrollToPage(pageNumber)
        }, 100)
      })
    }
  }

  function toggleFitPage() {
    if (fitPage.value) {
      // Switching from fit page to manual zoom
      // Set the current scale to the effective zoom level
      currentScale.value = effectiveZoom.value
      fitPage.value = false
    } else {
      // Switching to fit page
      fitPage.value = true
    }

    // Clear stored page heights when scale changes to prevent spacing issues
    pageHeights.value.clear()
  }

  function zoomIn() {
    const wasInFitMode = fitPage.value

    if (fitPage.value) {
      // If currently in fit page mode, switch to manual and start from effective zoom
      currentScale.value = Math.min(5, effectiveZoom.value + 0.1)
      fitPage.value = false
    } else {
      currentScale.value = Math.min(5, currentScale.value + 0.1)
    }

    // Clear stored page heights when scale changes to prevent spacing issues
    pageHeights.value.clear()
  }

  function zoomOut() {
    const wasInFitMode = fitPage.value

    if (fitPage.value) {
      // If currently in fit page mode, switch to manual and start from effective zoom
      currentScale.value = Math.max(0.1, effectiveZoom.value - 0.1)
      fitPage.value = false
    } else {
      currentScale.value = Math.max(0.1, currentScale.value - 0.1)
    }

    // Clear stored page heights when scale changes to prevent spacing issues
    pageHeights.value.clear()
  }

  function getPageRotation(pageNumber: number): number {
    return pageRotations.value.get(pageNumber) || 0
  }

  // Get rotation for a virtual page (by virtual page ID)
  function getVirtualPageRotation(virtualPageId: string): number {
    return virtualPageRotations.value.get(virtualPageId) || 0
  }

  // Calculate container width on resize and setup scroll tracking
  let resizeObserver: ResizeObserver | null = null

  onMounted(() => {
    if (containerRef.value) {
      resizeObserver = new ResizeObserver(() => {
        updateContainerWidth()
      })
      resizeObserver.observe(containerRef.value)
    }

    document.addEventListener('keydown', handleKeydown)

    // Initial container width calculation
    updateContainerWidth()
  })

  onUnmounted(() => {
    if (resizeObserver && containerRef.value) {
      resizeObserver.unobserve(containerRef.value)
      resizeObserver.disconnect()
    }

    // Clear loading timeout
    if (loadingTimeout.value) {
      clearTimeout(loadingTimeout.value)
      loadingTimeout.value = null
    }

    // Clear scale calculation timeout
    if (scaleCalculationTimeout.value) {
      clearTimeout(scaleCalculationTimeout.value)
      scaleCalculationTimeout.value = null
    }

    // Clear scroll stop timeout
    if (scrollStopTimeout.value) {
      clearTimeout(scrollStopTimeout.value)
      scrollStopTimeout.value = null
    }

    // Restore background scrolling in case component is unmounted while modal is open
    document.body.style.overflow = ''

    // Clean up modified PDF URL
    if (modifiedPdfUrl.value) {
      URL.revokeObjectURL(modifiedPdfUrl.value)
    }

    document.removeEventListener('keydown', handleKeydown)
    pageRefs.value.clear()
  })

  function updateCurrentPage(newPage: number) {
    currentPage.value = newPage
    lastStablePage.value = newPage
    pageChangeCooldown.value = Date.now() // Reset cooldown for manual navigation
    emit('pageChange', newPage)

    // If user navigates while loading and the target page is already loaded, hide loading
    if (isLoading.value && loadedPages.value.has(newPage)) {
      isLoading.value = false

      // Clear the loading timeout since we successfully loaded
      if (loadingTimeout.value) {
        clearTimeout(loadingTimeout.value)
        loadingTimeout.value = null
      }
    }

    scrollToPage(newPage)
  }

  function scrollToPage(pageNumber: number) {
    if (!scrollContainerRef.value) {
      return
    }

    const pageElement = pageRefs.value.get(pageNumber)
    if (pageElement) {
      isScrolling.value = true
      pageElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
      setTimeout(() => {
        isScrolling.value = false
      }, 1000)
    } else {
      // Retry after a short delay if the page element isn't ready yet
      setTimeout(() => {
        const retryPageElement = pageRefs.value.get(pageNumber)
        if (retryPageElement) {
          isScrolling.value = true
          retryPageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          })
          setTimeout(() => {
            isScrolling.value = false
          }, 1000)
        }
      }, 500)
    }
  }

  function setPageRef(pageNumber: number, el: HTMLElement | null) {
    if (el) {
      pageRefs.value.set(pageNumber, el)
    } else {
      pageRefs.value.delete(pageNumber)
    }
  }

  function handleScroll() {
    if (isScrolling.value || !scrollContainerRef.value) return

    isActivelyScrolling.value = true

    if (scrollStopTimeout.value) {
      clearTimeout(scrollStopTimeout.value)
    }

    scrollStopTimeout.value = setTimeout(() => {
      isActivelyScrolling.value = false
      if (fitPage.value) {
        debouncedCalculateFitToPageScale()
      }
    }, 300)

    const container = scrollContainerRef.value
    const containerTop = container.scrollTop
    const containerHeight = container.clientHeight
    const centerY = containerTop + containerHeight / 2

    let closestPageOrVirtual = 1
    let closestDistance = Infinity

    pageRefs.value.forEach((pageElement, pageNumber) => {
      const rect = pageElement.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const pageTop = rect.top - containerRect.top + containerTop
      const pageCenter = pageTop + rect.height / 2
      const distance = Math.abs(pageCenter - centerY)

      if (distance < closestDistance) {
        closestDistance = distance
        closestPageOrVirtual = pageNumber
      }
    })

    const now = Date.now()
    const timeSinceLastChange = now - pageChangeCooldown.value
    const cooldownPeriod = 1000
    const pageDifference = Math.abs(closestPageOrVirtual - currentPage.value)
    const canChangePage = timeSinceLastChange > cooldownPeriod || pageDifference > 1

    if (virtualPages.value.length > 0) {
      if (closestPageOrVirtual !== currentVirtualPage.value && canChangePage) {
        currentVirtualPage.value = closestPageOrVirtual
        lastStablePage.value = closestPageOrVirtual
        pageChangeCooldown.value = now

        const virtualPage = virtualPages.value.find(
          (vp) => vp.virtualIndex === closestPageOrVirtual
        )
        if (virtualPage && virtualPage.originalPage !== currentPage.value) {
          currentPage.value = virtualPage.originalPage
          emit('pageChange', virtualPage.originalPage)
        }
      }
    } else {
      if (closestPageOrVirtual !== currentPage.value && canChangePage) {
        currentPage.value = closestPageOrVirtual
        lastStablePage.value = closestPageOrVirtual
        pageChangeCooldown.value = now
        emit('pageChange', closestPageOrVirtual)
      }
    }
  }

  function onHighlight(event: any) {
    emit('highlight', event)
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      closeViewer()
    }
  }

  function handleModalClick(event: MouseEvent) {
    // Close edit menu when clicking outside the dropdown
    const target = event.target as HTMLElement
    if (!target.closest('.edit-dropdown') && !target.closest('.edit-button')) {
      showEditMenu.value = false
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (!props.isOpen || (event.target as HTMLElement)?.tagName === 'INPUT') {
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        if (currentVirtualPage.value > 1) {
          navigateToVirtualPage(currentVirtualPage.value - 1)
        }
        break
      case 'ArrowRight':
        event.preventDefault()
        if (currentVirtualPage.value < (totalVirtualPages.value || pages.value)) {
          navigateToVirtualPage(currentVirtualPage.value + 1)
        }
        break
      case 'Escape':
        event.preventDefault()
        if (isEdgeAdjustMode.value) {
          closeEdgeAdjustMode()
        } else {
          closeViewer()
        }
        break
    }
  }

  function navigateToVirtualPage(virtualIndex: number) {
    const virtualPage = virtualPages.value.find((vp) => vp.virtualIndex === virtualIndex)
    if (virtualPage) {
      currentVirtualPage.value = virtualIndex
      currentPage.value = virtualPage.originalPage
    }
  }

  function rotateClipPathCoordinates(region: PageRegion, rotation: number) {
    // Transform coordinates based on page rotation
    // CSS rotation transforms happen around the center, so we need to adjust accordingly

    const corners = [region.top_left, region.top_right, region.bottom_right, region.bottom_left]
    const transformedCorners = corners.map((corner) => {
      switch (rotation) {
        case 90:
          // 90° clockwise: (x, y) -> (100 - y, x)
          return { x: 100 - corner.y, y: corner.x }
        case 180:
          // 180°: (x, y) -> (100 - x, 100 - y)
          return { x: 100 - corner.x, y: 100 - corner.y }
        case 270:
          // 270° clockwise: (x, y) -> (y, 100 - x)
          return { x: corner.y, y: 100 - corner.x }
        default:
          return corner
      }
    })

    return {
      topLeft: transformedCorners[0],
      topRight: transformedCorners[1],
      bottomRight: transformedCorners[2],
      bottomLeft: transformedCorners[3]
    }
  }

  // Get styles for virtual page including rotation transform
  function getVirtualPageStyleWithRotation(virtualPage: virtual_page) {
    const rotation = getVirtualPageRotation(virtualPage.id)
    const pageRotation = getPageRotation(virtualPage.originalPage)
    const region = virtualPage.region

    // Adjust clip path coordinates based on page rotation
    let adjustedCorners = {
      topLeft: region.top_left,
      topRight: region.top_right,
      bottomLeft: region.bottom_left,
      bottomRight: region.bottom_right
    }

    // If the page is rotated, we need to adjust the clip path coordinates
    if (pageRotation !== 0) {
      adjustedCorners = rotateClipPathCoordinates(region, pageRotation)
    }

    // Generate clip path using polygon for 4-corner regions
    const clipPath = `polygon(${adjustedCorners.topLeft.x}% ${adjustedCorners.topLeft.y}%, ${adjustedCorners.topRight.x}% ${adjustedCorners.topRight.y}%, ${adjustedCorners.bottomRight.x}% ${adjustedCorners.bottomRight.y}%, ${adjustedCorners.bottomLeft.x}% ${adjustedCorners.bottomLeft.y}%)`

    const styles: any = {
      maxWidth: fitPage.value ? '100%' : 'none',
      height: 'auto',
      clipPath
    }

    if (rotation !== 0) {
      styles.transform = `rotate(${rotation}deg)`
      styles.transformOrigin = 'center center'
    }

    return styles
  }

  function hasRegionChanges(): boolean {
    // Check if regions have changed
    if (customRegions.value.size !== originalRegionsState.value.size) {
      return true
    }

    for (const [pageNum, currentRegions] of customRegions.value) {
      const originalRegions = originalRegionsState.value.get(pageNum) || []

      if (currentRegions.length !== originalRegions.length) {
        return true
      }

      // Deep comparison of regions
      for (let i = 0; i < currentRegions.length; i++) {
        const current = currentRegions[i]
        const original = originalRegions[i]

        if (
          !original ||
          current.topLeft.x !== original.topLeft.x ||
          current.topLeft.y !== original.topLeft.y ||
          current.topRight.x !== original.topRight.x ||
          current.topRight.y !== original.topRight.y ||
          current.bottomLeft.x !== original.bottomLeft.x ||
          current.bottomLeft.y !== original.bottomLeft.y ||
          current.bottomRight.x !== original.bottomRight.x ||
          current.bottomRight.y !== original.bottomRight.y ||
          current.id !== original.id
        ) {
          return true
        }
      }
    }

    return false
  }

  function closeEdgeAdjustMode() {
    // Check for unsaved changes
    if (hasRegionChanges()) {
      hasUnsavedChanges.value = true
      showApplyChangesDialog.value = true
      return
    }

    // No changes, close directly
    forceCloseEdgeAdjustMode()
  }

  function forceCloseEdgeAdjustMode() {
    isEdgeAdjustMode.value = false
    hasUnsavedChanges.value = false
    showApplyChangesDialog.value = false

    // Restore background scrolling
    document.body.style.overflow = ''

    // Cleanup drawing state
    if (currentDrawRect.value && regionOverlay.value) {
      ;(regionOverlay.value as HTMLElement).removeChild(currentDrawRect.value)
      currentDrawRect.value = null
    }
    isDrawing.value = false
  }

  // Image Editor Functions
  async function openImageEditor() {
    if (!isPdfReady.value) return

    // Check if we need to force a PDF reload to ensure we're using the correct version
    const expectedUrl = currentPdfUrl.value
    const currentSource = pdfSource.value

    // Extract the base URL without query parameters for comparison
    const expectedBaseUrl = expectedUrl.split('?')[0]
    const currentBaseUrl = currentSource.split('?')[0]

    if (expectedBaseUrl !== currentBaseUrl) {
      // Force PDF reload
      pdfKey.value++

      // Wait a bit for the PDF to reload
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Check if PDF is ready after reload
      if (!isPdfReady.value) {
        console.error('PDF not ready after forced reload')
        return
      }
    }

    captureCurrentPage()
  }

  // Function to open image editor for a specific page
  async function openImageEditorForPage(pageNumber: number) {
    if (!isPdfReady.value) return

    // Track edit page clicked
    trackEvent(MetricEventType.PDF_VIEWER_EDIT_PAGE_CLICKED, {
      page_number: pageNumber,
      document_uuid: props.document?.uuid,
      document_name: props.filename,
      has_modifications: hasAnyModifications.value,
      total_pages: totalPages.value
    })

    // Set the current page to the target page
    currentPage.value = pageNumber

    // Wait a bit for the page change to take effect
    await nextTick()

    // Open the image editor
    openImageEditor()
  }

  // New action functions
  function downloadPdf() {
    showActionsMenu.value = false

    // Track download clicked
    trackEvent(MetricEventType.PDF_VIEWER_DOWNLOAD_CLICKED, {
      document_uuid: props.document?.uuid,
      document_name: props.filename,
      has_modifications: hasAnyModifications.value,
      total_pages: totalPages.value
    })

    // Use the document's URL property - backend now automatically serves modified version
    const documentUrl = (props.document as any)?.url
    if (!documentUrl) {
      console.error('No document URL available for download')
      return
    }

    // Backend now handles filename with _modified suffix automatically
    const filename = props.filename || 'document.pdf'

    // Use the downloadDocument utility function
    downloadDocument(documentUrl, filename)
  }

  function openOrganizePages() {
    if (!props.document?.uuid) {
      console.error('No document UUID available for organizing pages')
      // Could show a toast notification here if needed
      return
    }

    const caseId = getCurrentCaseId()
    if (!caseId) {
      console.error('No case ID available for organizing pages')
      // Could show a toast notification here if needed
      return
    }

    // Track organize pages clicked
    trackEvent(MetricEventType.PDF_VIEWER_ORGANIZE_PAGES_CLICKED, {
      document_uuid: props.document.uuid,
      document_name: props.filename,
      current_page: currentPage.value,
      total_pages: totalPages.value,
      has_modifications: hasAnyModifications.value,
      case_uuid: caseId
    })

    try {
      // Navigate to the OrganizeDocumentsView with the current document
      router.push({
        name: 'Organize Pages',
        params: {
          caseId
        },
        state: {
          documentIds: [props.document.uuid]
        }
      })
    } catch (error) {
      console.error('Failed to navigate to organize pages:', error)
    }
  }

  // Helper function to get current case ID from route or store
  function getCurrentCaseId() {
    // Try to get case ID from current route
    const route = router.currentRoute.value

    if (route.params.caseId) {
      return route.params.caseId as string
    }

    // Fallback: try to extract from URL path - look for the case UUID pattern
    // The route structure is /app/cases/:caseId/... so we need to match the UUID
    const pathMatch = route.path.match(/\/cases\/([a-f0-9-]{36})/)
    if (pathMatch) {
      return pathMatch[1]
    }

    // Additional fallback: try to get from route params with different names
    if (route.params.uuid) {
      return route.params.uuid as string
    }

    console.warn('Could not determine case ID for organize pages')
    return ''
  }

  function confirmResetEdits() {
    // Track reset edits started
    trackEvent(MetricEventType.PDF_VIEWER_RESET_EDITS_STARTED, {
      document_uuid: props.document?.uuid,
      document_name: props.filename,
      has_local_modifications: isModified.value,
      has_backend_modifications: hasBackendModifications.value,
      total_pages: totalPages.value
    })

    showResetConfirmModal.value = true
  }

  function resetEdits() {
    // Track reset edits completed
    trackEvent(MetricEventType.PDF_VIEWER_RESET_EDITS_COMPLETED, {
      document_uuid: props.document?.uuid,
      document_name: props.filename,
      reset_type: hasBackendModifications.value ? 'backend' : 'local'
    })

    revertToOriginalPdf()
    showResetConfirmModal.value = false
  }

  function cancelResetEdits() {
    // Track reset edits cancelled
    trackEvent(MetricEventType.PDF_VIEWER_RESET_EDITS_CANCELLED, {
      document_uuid: props.document?.uuid,
      document_name: props.filename
    })

    showResetConfirmModal.value = false
  }

  // Close menus when clicking outside
  function handleDocumentClick(event: Event) {
    const target = event.target as HTMLElement
    if (!target.closest('.relative')) {
      showActionsMenu.value = false
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleDocumentClick)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleDocumentClick)
  })

  async function captureCurrentPage() {
    if (!currentPage.value) {
      console.error('Current page not available for capture')
      return
    }

    isCapturingPage.value = true

    try {
      // Wait a bit for the page to be fully rendered
      await nextTick()

      // Try multiple approaches to find the canvas
      let pdfCanvas: HTMLCanvasElement | null = null

      // ALWAYS use PDF.js high-resolution rendering - no fallbacks to display canvas
      if (!pdf.value) {
        console.error('PDF not available for high-resolution capture')
        return
      }

      try {
        // Create a temporary canvas for high-resolution rendering
        const tempCanvas = document.createElement('canvas')
        const tempCtx = tempCanvas.getContext('2d')

        if (!tempCtx) {
          console.error('Failed to get 2D context for high-res rendering')
          return
        }

        // Get the actual PDF document from the loading task
        let pdfDocument: any = pdf.value
        if (
          pdf.value &&
          typeof (pdf.value as any).getPage !== 'function' &&
          (pdf.value as any).promise
        ) {
          // If pdf.value is a loading task, get the actual document
          pdfDocument = await (pdf.value as any).promise
        }

        if (!pdfDocument || typeof pdfDocument.getPage !== 'function') {
          console.error('Invalid PDF document object:', pdfDocument)
          return
        }

        // Get the page from PDF.js
        const page = await pdfDocument.getPage(currentPage.value)
        // Use high resolution scale for crisp image capture
        const highResScale = 2.0 // 2x resolution for good quality
        const viewport = page.getViewport({ scale: highResScale })
        tempCanvas.width = viewport.width
        tempCanvas.height = viewport.height

        // Render the page to the canvas
        await page.render({
          canvasContext: tempCtx,
          viewport: viewport
        }).promise

        // Use this high-resolution canvas as our source
        pdfCanvas = tempCanvas
      } catch (error) {
        console.error('Failed to render page directly at high resolution:', error)
        return
      }

      // No fallbacks - we MUST use the high-resolution PDF.js rendering
      if (!pdfCanvas) {
        console.error('Failed to create high-resolution canvas from PDF.js')
        return
      }
      // Use the high-resolution canvas directly - no additional scaling needed
      const captureCanvas = document.createElement('canvas')
      const ctx = captureCanvas.getContext('2d')

      if (!ctx) {
        console.error('Failed to get 2D context')
        return
      }

      // Use the full resolution from PDF.js
      captureCanvas.width = pdfCanvas.width
      captureCanvas.height = pdfCanvas.height

      // Draw the high-resolution PDF.js canvas directly
      ctx.drawImage(pdfCanvas, 0, 0)

      // Convert to data URL
      const imageData = captureCanvas.toDataURL('image/png', 1.0)
      // Store the captured image data and show editor
      capturedPageImage.value = imageData
      showImageEditor.value = true
    } catch (error) {
      console.error('Error capturing page:', error)
    } finally {
      isCapturingPage.value = false
    }
  }

  async function handleImageEdit(editedImageData: string) {
    try {
      // For single image edits, just close the editor for now
      // The PDF replacement will be handled by handlePdfPageReplacement
      showImageEditor.value = false
    } catch (error) {
      console.error('Error applying page edit:', error)
    }
  }

  async function handlePdfPageReplacement(data: {
    pageNumber: number
    croppedPages: CroppedPageData[]
  }) {
    try {
      // Set loading state
      isApplyingChanges.value = true

      // Use the current PDF source (backend modified > local modified > original)
      const pdfUrlToUse = currentPdfUrl.value
      if (!pdfUrlToUse) {
        throw new Error('No PDF URL available')
      }

      // Fetch the current PDF
      const response = await fetch(pdfUrlToUse)
      if (!response.ok) {
        throw new Error('Failed to fetch PDF')
      }

      const pdfBytes = new Uint8Array(await response.arrayBuffer())

      // Replace the page with cropped images (convert to 0-based index)
      const pageIndex = data.pageNumber - 1
      const modifiedPdfBytes = await pdfEditor.replacePdfPageWithCroppedImages(
        pdfBytes,
        pageIndex,
        data.croppedPages
      )

      // Create a new blob URL for the modified PDF
      const modifiedBlob = pdfEditor.getPdfBlob(modifiedPdfBytes)

      // Clean up previous modified URL if it exists
      if (modifiedPdfUrl.value) {
        URL.revokeObjectURL(modifiedPdfUrl.value)
      }

      const newUrl = URL.createObjectURL(modifiedBlob)

      // Store the modified PDF URL and mark as modified
      modifiedPdfUrl.value = newUrl
      isModified.value = true

      // Upload the modified PDF to the backend (this will update the document prop)
      await uploadModifiedPdfToBackend(modifiedPdfBytes)

      // Clear page state for the edited page before reload
      clearPageState(data.pageNumber)

      // Force PDF reload by incrementing key and updating URL
      pdfKey.value++
      internalUrl.value = newUrl
      await nextTick()

      // Wait for PDF to start loading before closing editor
      await new Promise((resolve) => setTimeout(resolve, 100))

      // Close the image editor
      showImageEditor.value = false
    } catch (error) {
      console.error('Error replacing PDF page:', error)
      // Show error to user
      alert('Failed to replace PDF page. Please try again.')
    } finally {
      // Delay clearing the loading state to ensure PDF transition is complete
      await new Promise((resolve) => setTimeout(resolve, 200))
      isApplyingChanges.value = false

      // Trigger fit scale calculation now that PDF is stable
      if (fitPage.value) {
        await nextTick()
        calculateFitToPageScale()
      }
    }
  }

  function clearPageState(pageNumber: number) {
    // Clear page-specific state
    pageRotations.value.delete(pageNumber)
    pageScales.value.delete(pageNumber)
    highContrastPages.value.delete(pageNumber)
    loadedPages.value.delete(pageNumber)
    pageRefs.value.delete(pageNumber)

    // Clear virtual page rotations that reference this page
    const virtualKeysToDelete: string[] = []
    virtualPageRotations.value.forEach((rotation, key) => {
      if (key.includes(`-${pageNumber}-`)) {
        virtualKeysToDelete.push(key)
      }
    })
    virtualKeysToDelete.forEach((key) => virtualPageRotations.value.delete(key))
  }

  function clearAllPageState() {
    // Clear all page-specific state
    pageRotations.value.clear()
    virtualPageRotations.value.clear()
    pageScales.value.clear()
    highContrastPages.value.clear()
    loadedPages.value.clear()
    pageRefs.value.clear()

    // Reset scroll state
    hasInitialScrolled.value = false
  }

  async function uploadModifiedPdfToBackend(pdfBytes: Uint8Array) {
    try {
      if (!props.document?.uuid) {
        console.error('No document UUID available for upload')
        return
      }

      // Create a File object from the PDF bytes
      const pdfBlob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })
      const pdfFile = new File([pdfBlob], `${props.filename}_modified.pdf`, {
        type: 'application/pdf'
      })

      const formSignatures = props.formId ? signatureStore.getFormSignatures(props.formId) : {}

      // Use the generated service which handles CSRF tokens automatically
      const formData: { file: File; signatures?: string } = {
        file: pdfFile
      }
      if (Object.keys(formSignatures).length > 0) {
        formData.signatures = JSON.stringify(formSignatures)
      }

      const response = await DocumentsService.documentsUploadModifiedCreate({
        uuid: props.document.uuid,
        formData: formData as any
      })

      // Set flag to prevent cascading watcher triggers
      isUpdatingFromBackend.value = true

      // Emit event to notify parent components that document was updated
      emit('document-updated', response)

      // Clear flag after a short delay to allow all updates to complete
      nextTick(() => {
        setTimeout(() => {
          isUpdatingFromBackend.value = false
        }, 100)
      })
    } catch (error) {
      console.error('Error uploading modified PDF to backend:', error)
      // Don't throw the error - we want the UI update to continue even if backend upload fails
    }
  }

  async function revertToOriginalPdf() {
    try {
      if (!props.document?.uuid) {
        console.error('No document UUID available for revert')
        return
      }

      // Set loading state
      isRevertingChanges.value = true

      // Use the generated service which handles CSRF tokens automatically
      const result = await DocumentsService.documentsRevertToOriginalCreate({
        uuid: props.document.uuid
      })

      // Clean up modified PDF URL
      if (modifiedPdfUrl.value) {
        URL.revokeObjectURL(modifiedPdfUrl.value)
        modifiedPdfUrl.value = ''
      }

      // Reset to original PDF
      isModified.value = false

      // Clear all page state since we're reverting
      clearAllPageState()

      // Force PDF reload by incrementing key and setting original URL
      pdfKey.value++
      internalUrl.value = originalPdfUrl.value
      await nextTick()

      // Wait for PDF to start loading
      await new Promise((resolve) => setTimeout(resolve, 100))

      // Set flag to prevent cascading watcher triggers
      isUpdatingFromBackend.value = true

      // Emit event to notify parent components that document was updated
      emit('document-updated', result)

      // Clear flag after a short delay to allow all updates to complete
      nextTick(() => {
        setTimeout(() => {
          isUpdatingFromBackend.value = false
        }, 100)
      })
    } catch (error) {
      console.error('Error reverting to original PDF:', error)
    } finally {
      // Delay clearing the loading state to ensure PDF transition is complete
      await new Promise((resolve) => setTimeout(resolve, 200))
      isRevertingChanges.value = false

      // Trigger fit scale calculation now that PDF is stable
      if (fitPage.value) {
        await nextTick()
        calculateFitToPageScale()
      }
    }
  }
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm"
    @click="handleBackdropClick"
  >
    <div
      ref="containerRef"
      class="flex h-[90vh] w-[60vw] flex-col overflow-hidden rounded-lg bg-white shadow-lg"
      @click="handleModalClick"
    >
      <!-- Header -->
      <div
        class="toolbar-container flex h-24 shrink-0 flex-col border-b border-gray-200 bg-white px-6 py-4"
      >
        <!-- Top row: Title and close buttons -->
        <div class="mb-2 flex h-10 items-center justify-between">
          <!-- Title with edit icon -->
          <div class="flex items-center gap-2">
            <h2 class="text-base font-medium text-gray-900">{{ filename }}</h2>
          </div>

          <!-- Close button -->
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 bg-white
              text-gray-400 transition hover:bg-gray-50 hover:text-gray-600"
            @click="closeViewer"
          >
            <span class="sr-only">{{ t('close') }}</span>
            <X class="h-4 w-4" />
          </button>
        </div>

        <!-- Bottom row: Action buttons -->
        <div
          v-if="!props.hideTopbar"
          class="flex h-10 items-center justify-between"
        >
          <div class="flex items-center space-x-3">
            <!-- Organize pages button -->
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger as-child>
                  <button
                    class="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-all"
                    :class="[
                      isPdfReady
                        ? 'cursor-pointer border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        : 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                    ]"
                    :disabled="!isPdfReady"
                    @click="openOrganizePages"
                  >
                    <FileStack class="h-4 w-4" />
                    <span>{{ t('organizePages') }}</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  v-if="!isPdfReady"
                  side="bottom"
                  class="max-w-xs"
                >
                  <p class="text-sm">Please wait for the document to fully load.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <!-- Reset edits button - only show when there are modifications -->
            <TooltipProvider v-if="hasAnyModifications">
              <Tooltip>
                <TooltipTrigger as-child>
                  <button
                    class="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-all"
                    :class="[
                      isPdfReady && !isRevertingChanges
                        ? 'cursor-pointer border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        : 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                    ]"
                    :disabled="!isPdfReady || isRevertingChanges"
                    @click="confirmResetEdits"
                  >
                    <Undo2 class="h-4 w-4" />
                    <span>
                      {{ hasBackendModifications ? 'Revert to original' : 'Reset edits' }}
                    </span>
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  v-if="!isPdfReady"
                  side="bottom"
                  class="max-w-xs"
                >
                  <p class="text-sm">Please wait for the document to fully load.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <!-- Right side: 3 dots menu -->
          <div class="relative">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger as-child>
                  <button
                    class="inline-flex h-8 w-8 items-center justify-center rounded border border-gray-300 bg-white
                      text-gray-400 transition hover:bg-gray-50 hover:text-gray-600"
                    @click="showActionsMenu = !showActionsMenu"
                  >
                    <span class="sr-only">Document actions</span>
                    <MoreHorizontal class="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p class="text-sm">Document actions</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <!-- Actions dropdown menu -->
            <div
              v-if="showActionsMenu"
              class="absolute right-0 top-full z-[70] mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg"
            >
              <div class="p-1">
                <button
                  class="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  @click="downloadPdf"
                >
                  <Download class="h-4 w-4" />
                  <span>Download PDF</span>
                </button>
                <!-- <button
                  class="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  @click="deleteDocument"
                >
                  <Trash2 class="h-4 w-4" />
                  <span>Delete document</span>
                </button> -->
              </div>
            </div>
          </div>
        </div>
        <slot name="customToolbar" />
      </div>

      <!-- Content -->
      <div class="relative flex-1 overflow-hidden bg-white">
        <div
          v-if="isLoading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white/90"
        >
          <div class="flex flex-col items-center gap-3">
            <div
              class="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"
            ></div>
            <div class="text-center">
              <div class="text-sm font-medium text-gray-600">{{ t('loadingDocument') }}</div>
              <div
                v-if="loadingProgress > 0"
                class="mt-1 text-xs text-gray-500"
              >
                {{ loadingProgress }}{{ t('percentLoaded') }}
              </div>
            </div>
          </div>
        </div>

        <!-- Applying changes overlay -->
        <div
          v-if="isApplyingChanges"
          class="absolute inset-0 z-20 flex items-center justify-center bg-white/95"
        >
          <div class="flex flex-col items-center gap-3">
            <div
              class="h-6 w-6 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"
            ></div>
            <div class="text-center">
              <div class="text-sm font-medium text-gray-700">{{ t('applyingChanges') }}</div>
              <div class="mt-1 text-xs text-gray-500">{{ t('processingPdf') }}</div>
            </div>
          </div>
        </div>

        <!-- Reverting changes overlay -->
        <div
          v-if="isRevertingChanges"
          class="absolute inset-0 z-20 flex items-center justify-center bg-white/95"
        >
          <div class="flex flex-col items-center gap-3">
            <div
              class="h-6 w-6 animate-spin rounded-full border-2 border-purple-600 border-t-transparent"
            ></div>
            <div class="text-center">
              <div class="text-sm font-medium text-gray-700">{{ t('revertingChanges') }}</div>
              <div class="mt-1 text-xs text-gray-500">{{ t('loadingOriginalPdf') }}</div>
            </div>
          </div>
        </div>
        <div
          ref="scrollContainerRef"
          class="h-full w-full overflow-auto"
          @scroll="handleScroll"
        >
          <div
            class="flex w-full flex-col items-center justify-start py-6"
            :class="fitPage ? 'min-h-full' : 'min-h-full'"
          >
            <div class="flex w-full flex-col gap-6 px-6">
              <!-- Render virtual pages if they exist, otherwise render original pages -->
              <template v-if="virtualPages.length > 0">
                <div
                  v-for="virtualPage in virtualPages"
                  :key="`virtual-${virtualPage.id}`"
                  :ref="(el) => setPageRef(virtualPage.virtualIndex, el as HTMLElement)"
                  class="transition-all duration-300"
                  :class="getHighContrastClasses(virtualPage.originalPage)"
                >
                  <div class="relative">
                    <VuePDF
                      :key="`virtual-pdf-${pdfKey}-${virtualPage.virtualIndex}-${fitPage ? 'fit' : currentScale}-${getPageRotation(virtualPage.originalPage)}-${getVirtualPageRotation(virtualPage.id)}-${editMode}`"
                      :pdf="pdf"
                      :page="virtualPage.originalPage"
                      :scale="fitPage ? calculatedFitScale : currentScale"
                      :rotation="getPageRotation(virtualPage.originalPage)"
                      :highlight-text="highlightText"
                      :highlight-options="highlightOptions"
                      :fit-parent="false"
                      text-layer
                      :annotation-layer="withAnnotations"
                      :annotations-map="withAnnotations ? pdfStateAnnotationMap : {}"
                      class="mx-auto w-full shadow-lg"
                      :class="getHighContrastClasses(virtualPage.originalPage)"
                      :style="getVirtualPageStyleWithRotation(virtualPage)"
                      @loaded="(viewport) => onPdfLoaded(viewport, virtualPage.originalPage)"
                      @highlight="onHighlight"
                      @annotation="onAnnotation"
                    />

                    <template v-if="props.showFormFields">
                      <!-- Form field overlays for virtual pages -->
                      <FormFieldOverlayItem
                        v-for="field in props.formFields?.filter(
                          (f) => f.coordinates?.page === virtualPage.originalPage
                        ) || []"
                        :key="`virtual-field-${field.uuid}-${virtualPage.virtualIndex}`"
                        :field="field"
                        :zoom-level="effectiveZoom"
                        :is-selected="false"
                        :is-editable="false"
                        :is-fillable="true"
                        :field-value="getFieldValue(field.uuid)"
                        :signature-data="field.type === 'signature' ? getSignatureData(field) : ''"
                        :is-filled-by="isFilledBy"
                        @field-value-change="updateFieldValue"
                        @signature-click="handleSignatureClick"
                      />
                    </template>
                  </div>
                </div>
              </template>

              <!-- Render original pages when no virtual pages exist -->
              <template v-else>
                <div
                  v-for="page in pages"
                  :key="`page-${page}`"
                  :ref="(el) => setPageRef(page, el as HTMLElement)"
                  class="group relative flex w-full items-center justify-center transition-all duration-300"
                  :class="getHighContrastClasses(page)"
                >
                  <div class="group relative">
                    <VuePDF
                      :key="`pdf-${pdfKey}-${page}-${fitPage ? 'fit' : currentScale}-${getPageRotation(page)}-${editMode}`"
                      :pdf="pdf"
                      :page="page"
                      :scale="fitPage ? calculatedFitScale : currentScale"
                      :rotation="getPageRotation(page)"
                      :highlight-text="highlightText"
                      :highlight-options="highlightOptions"
                      :fit-parent="false"
                      text-layer
                      :annotation-layer="withAnnotations"
                      :annotations-map="withAnnotations ? pdfStateAnnotationMap : {}"
                      class="mx-auto w-full shadow-lg"
                      :class="getHighContrastClasses(page)"
                      :style="{
                        maxWidth: fitPage ? '100%' : 'none',
                        height: 'auto'
                      }"
                      @loaded="(viewport) => onPdfLoaded(viewport, page)"
                      @highlight="onHighlight"
                      @annotation="onAnnotation"
                    />

                    <!-- Form field overlays for this specific page -->
                    <FormFieldOverlayItem
                      v-for="field in props.formFields?.filter(
                        (f) => f.coordinates?.page === page
                      ) || []"
                      :key="`field-${field.uuid}-${page}`"
                      :field="field"
                      :zoom-level="effectiveZoom"
                      :is-selected="false"
                      :is-editable="false"
                      :is-fillable="true"
                      :field-value="getFieldValue(field.uuid)"
                      :signature-data="field.type === 'signature' ? getSignatureData(field) : ''"
                      :is-filled-by="isFilledBy"
                      @field-value-change="updateFieldValue"
                      @signature-click="handleSignatureClick"
                    />
                  </div>

                  <!-- Edit button -->
                  <div
                    v-if="isPdfReady && !props.disableEditPage"
                    class="absolute right-4 top-4 z-10"
                  >
                    <button
                      class="inline-flex items-center gap-1.5 rounded-lg border bg-white/90 px-3 py-2 text-sm font-semibold
                        shadow-lg backdrop-blur-sm transition-all hover:bg-white"
                      :class="[
                        isPdfReady
                          ? 'cursor-pointer border-gray-300 text-gray-700 hover:border-gray-400'
                          : 'cursor-not-allowed border-gray-200 text-gray-400'
                      ]"
                      :disabled="!isPdfReady"
                      @click="openImageEditorForPage(page)"
                    >
                      <FilePen class="h-4 w-4" />
                      <span>{{ t('editPage') }}</span>
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <!-- Footer -->
      <div
        class="flex h-20 shrink-0 items-center justify-between border-t border-gray-200 bg-white px-6"
      >
        <!-- Zoom controls -->
        <div class="flex items-center space-x-2">
          <button
            class="inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white
              text-black hover:bg-gray-50 hover:text-gray-800"
            title="Zoom out"
            @click="zoomOut"
          >
            <ZoomOut class="h-6 w-6" />
          </button>

          <span class="text-sm text-black">{{ displayZoom }}%</span>

          <button
            class="inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-300 bg-white
              text-black hover:bg-gray-50 hover:text-gray-800"
            title="Zoom in"
            @click="zoomIn"
          >
            <ZoomIn class="h-6 w-6" />
          </button>

          <button
            class="inline-flex h-10 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm
              text-black hover:bg-gray-50 hover:text-gray-800"
            title="Fit page"
            @click="toggleFitPage"
          >
            <Maximize class="h-6 w-6" />
            <span>Fit page</span>
          </button>
        </div>

        <!-- Page navigation -->
        <PDFNavigation
          :current-page="currentPage"
          :total-pages="pages"
          :current-virtual-page="currentVirtualPage"
          :total-virtual-pages="totalVirtualPages"
          :is-virtual-mode="virtualPages.length > 0"
          @update-page="updateCurrentPage"
          @update-virtual-page="navigateToVirtualPage"
        />
      </div>
    </div>
  </div>

  <!-- Image Editor Modal -->
  <ImageEditorModal
    :is-open="showImageEditor"
    :page-number="currentPage"
    :image-data="capturedPageImage"
    :document-name="filename"
    @close="showImageEditor = false"
    @apply="(editedImageData: string | any[]) => handleImageEdit(editedImageData as string)"
    @replace-pdf-page="handlePdfPageReplacement"
  />

  <!-- Signature Modal -->
  <SignModalV3
    v-if="showSignatureModal && currentSignatureField"
    :data="getSignatureData(currentSignatureField)"
    @close="
      () => {
        showSignatureModal = false
        signatureStore.showSignModal = false
      }
    "
    @save="
      (signatureData: any) => {
        updateFieldValue(currentSignatureField.uuid, signatureData)
        showSignatureModal = false
        signatureStore.showSignModal = false
      }
    "
  />

  <!-- Reset Edits Confirmation Modal -->
  <div
    v-if="showResetConfirmModal"
    class="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm"
    @click="cancelResetEdits"
  >
    <div
      class="w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
      @click.stop
    >
      <div class="mb-4">
        <h3 class="text-[20px] font-semibold text-gray-900">
          {{ hasBackendModifications ? 'Revert to original document?' : 'Reset all edits?' }}
        </h3>
        <p class="mt-2 text-[16px] text-gray-600">
          {{
            hasBackendModifications
              ? 'This will revert the document back to its original version, removing all modifications. This action cannot be undone.'
              : 'This will undo all page edits and revert the document to its original state. This action cannot be undone.'
          }}
        </p>
      </div>
      <div class="flex justify-end gap-3">
        <button
          class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium transition-all"
          :class="[
            isRevertingChanges
              ? 'cursor-not-allowed bg-gray-50 text-gray-400'
              : 'text-gray-700 hover:bg-gray-50'
          ]"
          :disabled="isRevertingChanges"
          @click="cancelResetEdits"
        >
          Cancel
        </button>
        <button
          class="rounded-lg px-4 py-2 text-sm font-medium text-white transition-all"
          :class="[
            isRevertingChanges ? 'cursor-not-allowed bg-gray-400' : 'bg-red-600 hover:bg-red-700'
          ]"
          :disabled="isRevertingChanges"
          @click="resetEdits"
        >
          Reset edits
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .overflow-auto {
    scroll-behavior: smooth;
  }

  .overflow-auto::-webkit-scrollbar {
    width: 8px;
  }

  .overflow-auto::-webkit-scrollbar-track {
    @apply rounded-lg bg-gray-100;
  }

  .overflow-auto::-webkit-scrollbar-thumb {
    @apply rounded-lg bg-gray-300;
  }

  .overflow-auto::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }

  /* Toggle switch styling */
  .toggle-switch {
    position: relative;
    width: 32px;
    height: 16px;
    background-color: #d1d5db;
    border-radius: 9999px;
    transition: background-color 0.2s;
    cursor: pointer;
  }

  .toggle-switch.active {
    background-color: #9a2bf5;
  }

  .toggle-switch-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    background-color: white;
    border-radius: 50%;
    transition: transform 0.2s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .toggle-switch.active .toggle-switch-thumb {
    transform: translateX(16px);
  }

  /* Ensure dropdown overlays PDF content properly */
  .edit-dropdown {
    position: absolute;
    z-index: 70;
    pointer-events: auto;
  }

  /* Ensure the toolbar container doesn't clip the dropdown */
  .toolbar-container {
    overflow: visible;
  }

  /* Edge adjustment specific styles */
  .region-overlay {
    pointer-events: none;
  }

  .region-overlay > * {
    pointer-events: auto;
  }

  .region-handle {
    width: 12px;
    height: 12px;
    border: 2px solid white;
    border-radius: 50%;
    position: absolute;
    transform: translate(-50%, -50%);
  }

  .region-handle.nw {
    top: 0;
    left: 0;
    cursor: nw-resize;
  }
  .region-handle.ne {
    top: 0;
    right: 0;
    cursor: ne-resize;
  }
  .region-handle.sw {
    bottom: 0;
    left: 0;
    cursor: sw-resize;
  }
  .region-handle.se {
    bottom: 0;
    right: 0;
    cursor: se-resize;
  }

  .region-selected {
    border-color: #8b5cf6 !important;
    box-shadow: 0 0 0 1px #8b5cf6;
  }

  .virtual-page-crop {
    overflow: hidden;
    transform-origin: top left;
  }

  .edge-adjust-modal {
    z-index: 80;
  }

  .drawing-rectangle {
    position: absolute;
    border: 2px dashed #3b82f6;
    background-color: rgba(59, 130, 246, 0.1);
    pointer-events: none;
  }

  .region-border {
    border: 2px dashed #8b5cf6;
    background-color: rgba(139, 92, 246, 0.2);
  }
</style>
